import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  Alert,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { formatOptionalNumber } from '@/utils/numbers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from '@/config/dayjs';
import { AccountBase } from '@/models/AccountViewModel';
import {
  useGetOfflineAccountQuery,
  useUpdateOfflineAccountMutation,
  accountsApi,
  HistoricalValue as ApiHistoricalValue,
} from '@/store/accountsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useDispatch } from 'react-redux';
import CustomCurrencyInput from '@/components/common/CustomCurrencyInput';
import { PAGE_SIZE_OPTIONS, PAGE_SIZES } from '@/constants/pagination';
import SingleDate from '@/components/date/SingleDate';
import { deriveHistoricalValueRanges } from '@/modules/portfolio/accountModal/ManualHistoricalValues/historicalValuesRanges';
import { useOfflineAccountResyncOnModalOpen } from '@/modules/portfolio/dialogs/hooks/useOfflineAccountResyncOnModalOpen';

interface HistoricalValuesTabProps {
  account: AccountBase;
  /** When false→true, refetch offline account and re-hydrate from API. */
  modalOpen: boolean;
  /**
   * Callback that fires whenever the historical values data mutates, signalling to the parent that the data changed.
   */
  onChange?: (hasChanges: boolean) => void; // callback to inform parent when data changes
  /**
   * Callback to inform the parent about the validity of the tab, mirroring legacy `isHistoricalValid` behaviour.
   */
  onValidationChange?: (valid: boolean) => void;
  /**
   * Optional callback to notify parent component whenever the loading state of this tab changes.
   */
  onLoadingChange?: (loading: boolean) => void;
}

export interface HistoricalValuesTabHandle {
  submit: () => Promise<void>;
  hasChanges: () => boolean;
  /** Returns the historical values payload for combined offline save. */
  getPayload: () => Promise<{
    HistoricalValues: {
      id?: number;
      FromDate: string;
      ToDate: string;
      Value: number;
    }[];
    DidHistoricalChange: boolean;
  }>;
}

interface HistoricalValue {
  id?: number;
  EffectiveDate: string; // MM/DD/YYYY
  Value: string;
}

interface HistoricalValueRow {
  id: number;
  EffectiveDate: string;
  Value: string;
}

const HistoricalValuesTab = forwardRef<
  HistoricalValuesTabHandle,
  HistoricalValuesTabProps
>(
  (
    { account, modalOpen, onChange, onValidationChange, onLoadingChange },
    ref,
  ) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(PAGE_SIZES.COMPACT);
    const [historicalValues, setHistoricalValues] = useState<HistoricalValue[]>(
      [],
    );
    const [payloadError, setPayloadError] = useState<string>('');
    // Store the initial snapshot to detect modifications later
    const initialValuesRef = useRef<HistoricalValue[] | null>(null);
    const [isValid, setIsValid] = useState(true);
    const [updateOfflineAccount, { isLoading: isUpdating }] =
      useUpdateOfflineAccountMutation();
    const dispatch = useDispatch();

    const isOfflineAccount = account.AccountType === 2;
    const isAsset = account.IsAsset; // Based on legacy UI logic
    const {
      data: offlineAccountData,
      isFetching: isFetchingOffline,
      error: offlineAccountError,
    } = useGetOfflineAccountQuery(
      isOfflineAccount ? account.AccountId : skipToken,
    );

    // Calculate overall loading state
    const isLoading = (isOfflineAccount && isFetchingOffline) || isUpdating;

    // Notify parent of loading state changes
    useEffect(() => {
      onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    // Sync from API to form only once per modal open session.
    // Re-running on every offlineAccountData change (e.g. RTK Query refetch)
    // would overwrite user edits and make inputs appear non-editable.
    const hasInitializedFromOfflineData = useRef(false);

    useOfflineAccountResyncOnModalOpen(
      modalOpen,
      account.AccountId,
      isOfflineAccount,
      hasInitializedFromOfflineData,
    );

    const getHistoricalPayload = () => {
      setPayloadError('');
      const points = historicalValues
        .filter(
          (v) =>
            v.EffectiveDate &&
            v.Value !== '' &&
            v.Value !== '-' &&
            v.Value !== '.' &&
            v.Value !== '-.',
        )
        .map((v) => ({
          effectiveDate: v.EffectiveDate,
          value: parseFloat(v.Value) || 0,
        }));

      const { ranges, duplicateDateKeys } = deriveHistoricalValueRanges(points);

      if (duplicateDateKeys.length) {
        const msg =
          'Historical values contain duplicate effective dates. Please ensure each date is unique.';
        setPayloadError(msg);
        // Block save. Backend will reject duplicates and may return a generic 500.
        throw new Error(msg);
      }

      return {
        HistoricalValues: ranges,
        DidHistoricalChange: true,
      };
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        if (!isOfflineAccount || !offlineAccountData) return;

        await updateOfflineAccount({
          ...offlineAccountData,
          ...getHistoricalPayload(),
        }).unwrap();

        dispatch(
          accountsApi.util.invalidateTags([{ type: 'Accounts', id: 'LIST' }]),
        );
      },
      hasChanges: dataHasChanged,
      getPayload: async () => Promise.resolve(getHistoricalPayload()),
    }));

    // Initialize historical values once when offline data first loads.
    // For offline accounts, only sync from API once to avoid overwriting user edits on refetch.
    useEffect(() => {
      if (!isOfflineAccount) {
        const initialValue: HistoricalValue = {
          id: 0,
          EffectiveDate: dayjs().format('MM/DD/YYYY'),
          Value: account.Balance.toFixed(2),
        };
        setHistoricalValues([initialValue]);
        initialValuesRef.current = [initialValue];
        return;
      }

      if (!offlineAccountData || hasInitializedFromOfflineData.current) return;

      hasInitializedFromOfflineData.current = true;

      if (offlineAccountData.HistoricalValues?.length) {
        const initial = offlineAccountData.HistoricalValues.map(
          (v: ApiHistoricalValue, index: number) => {
            const effectiveDate = dayjs(v.FromDate);
            return {
              id: v.id ?? index,
              Value: String(v.Value),
              EffectiveDate: effectiveDate.isValid()
                ? effectiveDate.format('MM/DD/YYYY')
                : '',
            };
          },
        );
        setHistoricalValues(initial);
        initialValuesRef.current = initial;
      } else {
        const initialValue: HistoricalValue = {
          id: 0,
          EffectiveDate: dayjs().format('MM/DD/YYYY'),
          Value: account.Balance.toFixed(2),
        };
        setHistoricalValues([initialValue]);
        initialValuesRef.current = [initialValue];
      }
    }, [isOfflineAccount, offlineAccountData, account.Balance]);

    // Add a single empty row for editing (similar to legacy UI)
    const displayValues: HistoricalValueRow[] = useMemo(() => {
      const sorted = [...historicalValues].sort((a, b) => {
        const aDate = dayjs(a.EffectiveDate, 'MM/DD/YYYY', true);
        const bDate = dayjs(b.EffectiveDate, 'MM/DD/YYYY', true);

        // Put invalid/empty dates last (but above the always-present blank row)
        if (!aDate.isValid() && !bDate.isValid()) return 0;
        if (!aDate.isValid()) return 1;
        if (!bDate.isValid()) return -1;

        // Oldest first (ascending)
        return aDate.diff(bDate, 'day');
      });

      const rows: HistoricalValueRow[] = sorted.map((value, index) => ({
        id: value.id ?? index,
        EffectiveDate: value.EffectiveDate || '',
        Value: value.Value || '',
      }));

      rows.push({
        id: rows.length ? Math.max(...rows.map((r) => r.id)) + 1 : 0,
        EffectiveDate: '',
        Value: '',
      });

      return rows;
    }, [historicalValues]);

    const handleChangePage = (_event: unknown, newPage: number) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };

    const handleEffectiveDateChange = (rowId: number, date: string) => {
      setHistoricalValues((prevValues) => {
        const newValues = [...prevValues];
        const index = newValues.findIndex((v) => v.id === rowId);

        if (index >= 0) {
          newValues[index] = {
            ...newValues[index],
            EffectiveDate: date,
          };
        } else {
          newValues.push({
            id: rowId,
            EffectiveDate: date,
            Value: '0', // Set default value to 0 for manual entry
          });
        }

        return newValues.filter(
          (v) => v.EffectiveDate || (parseFloat(v.Value) || 0) !== 0,
        );
      });
    };

    const handleBalanceChange = (
      rowId: number,
      newBalance: string | undefined,
    ) => {
      const valueToSet = newBalance === undefined ? '' : newBalance;

      setHistoricalValues((prevValues) => {
        const newValues = [...prevValues];
        const index = newValues.findIndex((v) => v.id === rowId);

        if (index >= 0) {
          newValues[index] = {
            ...newValues[index],
            Value: valueToSet,
          };
        } else {
          newValues.push({
            id: rowId,
            EffectiveDate: dayjs().format('MM/DD/YYYY'),
            Value: valueToSet,
          });
        }

        // Validate all rows after update
        const allValid = newValues.every((v) => {
          // Empty/partial rows are considered valid until a value is committed.
          if (
            v.Value === '' ||
            v.Value === '-' ||
            v.Value === '.' ||
            v.Value === '-.'
          )
            return true;
          const numericValue = parseFloat(v.Value);
          if (isNaN(numericValue)) return true;
          return isAsset ? numericValue >= 0 : numericValue <= 0;
        });
        setIsValid(allValid);
        onValidationChange?.(allValid);

        return newValues.filter(
          (v) => v.EffectiveDate || (parseFloat(v.Value) || 0) !== 0,
        );
      });
    };

    const handleDeleteRow = (rowId: number) => {
      setHistoricalValues((prevValues) =>
        prevValues.filter((v) => v.id !== rowId),
      );
    };

    const paginatedData = displayValues.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    // Helper to determine if the data has changed compared to the initial snapshot
    function dataHasChanged() {
      if (!initialValuesRef.current) return false;

      const simplify = (vals: HistoricalValue[]) =>
        vals
          .map((v) => ({
            EffectiveDate: v.EffectiveDate,
            Value: v.Value,
          }))
          .sort((a, b) =>
            dayjs(a.EffectiveDate, 'MM/DD/YYYY', true).diff(
              dayjs(b.EffectiveDate, 'MM/DD/YYYY', true),
            ),
          );

      return (
        JSON.stringify(simplify(initialValuesRef.current)) !==
        JSON.stringify(simplify(historicalValues))
      );
    }

    // Notify parent whenever change state toggles
    const notifyParent = () => {
      onChange?.(dataHasChanged());
    };

    // Run notification effect whenever historicalValues changes
    useEffect(() => {
      notifyParent();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historicalValues]);

    // Notify parent of validity whenever it changes
    useEffect(() => {
      onValidationChange?.(isValid);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid]);

    // Show loading state while fetching offline account data
    if (isOfflineAccount && isFetchingOffline) {
      return (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight='200px'
        >
          <CircularProgress />
          <Typography
            variant='body2'
            sx={{ ml: 2 }}
          >
            Loading account details...
          </Typography>
        </Box>
      );
    }

    // Show error state if there's an error fetching offline account data
    if (isOfflineAccount && offlineAccountError) {
      return (
        <Alert
          severity='error'
          sx={{ m: 2 }}
        >
          Failed to load account details. Please try again.
        </Alert>
      );
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box>
          <Typography
            variant='body1'
            sx={{ mb: 2 }}
          >
            Current Value: $
            {formatOptionalNumber(account.Balance, '0.00', 'en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Typography>

          {isOfflineAccount && (
            <>
              <Alert
                severity='info'
                sx={{ mb: 2 }}
              >
                It can take up to 5 minutes for changes to historical values to
                appear.
              </Alert>
            </>
          )}

          {payloadError && (
            <Alert
              severity='error'
              sx={{ mb: 2 }}
            >
              {payloadError}
            </Alert>
          )}

          {!isOfflineAccount && (
            <Alert
              severity='info'
              sx={{ mb: 2 }}
            >
              Historical values can only be edited for offline accounts.
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ padding: '8px' }}>Date</TableCell>
                  <TableCell sx={{ padding: '8px' }}>Balance</TableCell>
                  <TableCell sx={{ padding: '8px' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row) => {
                  return (
                    <TableRow key={row.id}>
                      <TableCell sx={{ padding: '8px', width: '50%' }}>
                        <Box
                          sx={{
                            width: '100%',
                            '& .MuiFormControl-root': {
                              width: '100% !important',
                            },
                            '& .MuiTextField-root': {
                              width: '100% !important',
                            },
                            '& .MuiInputBase-root': {
                              width: '100% !important',
                            },
                            '& .MuiOutlinedInput-root': {
                              width: '100% !important',
                            },
                          }}
                        >
                          <SingleDate
                            date={row.EffectiveDate}
                            onChange={
                              isUpdating
                                ? undefined
                                : (date) => {
                                    handleEffectiveDateChange(row.id, date);
                                  }
                            }
                            label=''
                            disabled={!isOfflineAccount || isUpdating}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ padding: '8px', width: '30%' }}>
                        <CustomCurrencyInput
                          value={row.Value}
                          onValueChange={(value) => {
                            handleBalanceChange(row.id, value);
                          }}
                          prefix='$'
                          decimalsLimit={2}
                          allowDecimals
                          allowNegativeValue
                          disabled={
                            !isOfflineAccount ||
                            !row.EffectiveDate ||
                            isUpdating
                          }
                          fullWidth
                        />
                        {!isValid && (parseFloat(row.Value) || 0) !== 0 && (
                          <Typography
                            variant='caption'
                            color='error'
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            {`${isAsset ? 'Assets' : 'Liabilities'} must have ${
                              isAsset ? 'positive' : 'negative or zero'
                            } values`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ padding: '8px', width: '10%' }}>
                        <IconButton
                          onClick={() => {
                            handleDeleteRow(row.id);
                          }}
                          size='small'
                          disabled={
                            !isOfflineAccount ||
                            (!row.EffectiveDate && !row.Value) ||
                            isUpdating
                          }
                        >
                          <CloseIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[...PAGE_SIZE_OPTIONS.COMPACT]}
            component='div'
            count={displayValues.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          {isOfflineAccount && (
            <Typography
              variant='body2'
              sx={{ mt: 2, color: 'text.secondary' }}
            >
              New rows will automatically appear.
            </Typography>
          )}
        </Box>
      </LocalizationProvider>
    );
  },
);

HistoricalValuesTab.displayName = 'HistoricalValuesTab';

export default HistoricalValuesTab;
